import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import apiErrorsEn from "@/lib/errors/messages.en.json";
import apiErrorsJa from "@/lib/errors/messages.ja.json";
import apiErrorsTh from "@/lib/errors/messages.th.json";

import authEn from "./en/auth.json";
import contentEn from "./en/content.json";
import themeEditorEn from "./en/theme-editor.json";
import searchEn from "./en/search.json";
import searchJa from "./ja/search.json";
import searchTh from "./th/search.json";
import mediaEn from "./en/media.json";
import mediaJa from "./ja/media.json";
import mediaTh from "./th/media.json";
import dashboardEn from "./en/dashboard.json";
import dashboardJa from "./ja/dashboard.json";
import dashboardTh from "./th/dashboard.json";
import productsEn from "./en/products.json";
import productsJa from "./ja/products.json";
import productsTh from "./th/products.json";
import catalogEn from "./en/catalog.json";
import catalogJa from "./ja/catalog.json";
import catalogTh from "./th/catalog.json";
import collectionsEn from "./en/collections.json";
import collectionsJa from "./ja/collections.json";
import collectionsTh from "./th/collections.json";
import taxonomyEn from "./en/taxonomy.json";
import taxonomyJa from "./ja/taxonomy.json";
import taxonomyTh from "./th/taxonomy.json";
import inventoryEn from "./en/inventory.json";
import inventoryJa from "./ja/inventory.json";
import inventoryTh from "./th/inventory.json";
import importExportEn from "./en/import-export.json";
import importExportJa from "./ja/import-export.json";
import importExportTh from "./th/import-export.json";
import ordersEn from "./en/orders.json";
import ordersJa from "./ja/orders.json";
import ordersTh from "./th/orders.json";
import customersEn from "./en/customers.json";
import customersJa from "./ja/customers.json";
import customersTh from "./th/customers.json";
import discountsEn from "./en/discounts.json";
import discountsJa from "./ja/discounts.json";
import discountsTh from "./th/discounts.json";
import consignmentEn from "./en/consignment.json";
import consignmentJa from "./ja/consignment.json";
import consignmentTh from "./th/consignment.json";
import intakeEn from "./en/intake.json";
import intakeJa from "./ja/intake.json";
import intakeTh from "./th/intake.json";
import kobutsuEn from "./en/kobutsu.json";
import kobutsuJa from "./ja/kobutsu.json";
import kobutsuTh from "./th/kobutsu.json";
import payoutsEn from "./en/payouts.json";
import payoutsJa from "./ja/payouts.json";
import payoutsTh from "./th/payouts.json";
import posEn from "./en/pos.json";
import posJa from "./ja/pos.json";
import posTh from "./th/pos.json";
import taxEn from "./en/tax.json";
import taxJa from "./ja/tax.json";
import taxTh from "./th/tax.json";
import paymentsEn from "./en/payments.json";
import paymentsJa from "./ja/payments.json";
import paymentsTh from "./th/payments.json";
import shippingEn from "./en/shipping.json";
import shippingJa from "./ja/shipping.json";
import shippingTh from "./th/shipping.json";
import settingsEn from "./en/settings.json";
import settingsJa from "./ja/settings.json";
import settingsTh from "./th/settings.json";
import staffEn from "./en/staff.json";
import staffJa from "./ja/staff.json";
import staffTh from "./th/staff.json";
import systemEn from "./en/system.json";
import systemJa from "./ja/system.json";
import systemTh from "./th/system.json";
import notificationsEn from "./en/notifications.json";
import notificationsJa from "./ja/notifications.json";
import notificationsTh from "./th/notifications.json";
import helpEn from "./en/help.json";
import helpJa from "./ja/help.json";
import helpTh from "./th/help.json";
import authJa from "./ja/auth.json";
import contentJa from "./ja/content.json";
import themeEditorJa from "./ja/theme-editor.json";
import en from "./locales/en.json";
import ja from "./locales/ja.json";
import th from "./locales/th.json";
import authTh from "./th/auth.json";
import contentTh from "./th/content.json";
import themeEditorTh from "./th/theme-editor.json";

/**
 * Staff UI languages (design/04 §0/§1): ja / th / en, chosen per staff member and remembered in
 * localStorage; the first visit follows the browser (ja browser → ja). zh-Hant/zh-Hans come later.
 *
 * Namespaces: `common` (shell, shared components), `auth` (sign-in, profile), `apiErrors` (the
 * error dictionary for every `error.code` of the services — src/lib/errors), and the T09 screens
 * (`themeEditor`, `content`), and the T11 screens (`search`, `media`, `dashboard`, `products`,
 * `catalog`, `collections`, `taxonomy`, `importExport`, `inventory`, `orders`, `customers`,
 * `discounts`, `consignment`, `intake`, `payouts`, `kobutsu`, `pos`, `tax`, `payments`,
 * `shipping`, `settings`, `staff`, `system`, `notifications`, `help`).
 * `pnpm i18n:check` fails when ja/th miss a key of en.
 */
export const ADMIN_LOCALES = ["ja", "th", "en"] as const;
export type AdminLocale = (typeof ADMIN_LOCALES)[number];
export const LANGUAGE_STORAGE_KEY = "tcg.admin.lang";

export const NAMESPACES = [
  "common",
  "auth",
  "apiErrors",
  "themeEditor",
  "content",
  "search",
  "media",
  "dashboard",
  "products",
  "catalog",
  "collections",
  "taxonomy",
  "importExport",
  "inventory",
  "orders",
  "customers",
  "discounts",
  "consignment",
  "intake",
  "payouts",
  "kobutsu",
  "pos",
  "tax",
  "payments",
  "shipping",
  "settings",
  "staff",
  "system",
  "notifications",
  "help",
] as const;

export const resources = {
  en: {
    common: en,
    auth: authEn,
    apiErrors: apiErrorsEn,
    themeEditor: themeEditorEn,
    content: contentEn,
    search: searchEn,
    media: mediaEn,
    dashboard: dashboardEn,
    products: productsEn,
    catalog: catalogEn,
    collections: collectionsEn,
    taxonomy: taxonomyEn,
    importExport: importExportEn,
    inventory: inventoryEn,
    orders: ordersEn,
    customers: customersEn,
    discounts: discountsEn,
    consignment: consignmentEn,
    intake: intakeEn,
    payouts: payoutsEn,
    kobutsu: kobutsuEn,
    pos: posEn,
    tax: taxEn,
    payments: paymentsEn,
    shipping: shippingEn,
    settings: settingsEn,
    staff: staffEn,
    system: systemEn,
    notifications: notificationsEn,
    help: helpEn,
  },
  ja: {
    common: ja,
    auth: authJa,
    apiErrors: apiErrorsJa,
    themeEditor: themeEditorJa,
    content: contentJa,
    search: searchJa,
    media: mediaJa,
    dashboard: dashboardJa,
    products: productsJa,
    catalog: catalogJa,
    collections: collectionsJa,
    taxonomy: taxonomyJa,
    importExport: importExportJa,
    inventory: inventoryJa,
    orders: ordersJa,
    customers: customersJa,
    discounts: discountsJa,
    consignment: consignmentJa,
    intake: intakeJa,
    payouts: payoutsJa,
    kobutsu: kobutsuJa,
    pos: posJa,
    tax: taxJa,
    payments: paymentsJa,
    shipping: shippingJa,
    settings: settingsJa,
    staff: staffJa,
    system: systemJa,
    notifications: notificationsJa,
    help: helpJa,
  },
  th: {
    common: th,
    auth: authTh,
    apiErrors: apiErrorsTh,
    themeEditor: themeEditorTh,
    content: contentTh,
    search: searchTh,
    media: mediaTh,
    dashboard: dashboardTh,
    products: productsTh,
    catalog: catalogTh,
    collections: collectionsTh,
    taxonomy: taxonomyTh,
    importExport: importExportTh,
    inventory: inventoryTh,
    orders: ordersTh,
    customers: customersTh,
    discounts: discountsTh,
    consignment: consignmentTh,
    intake: intakeTh,
    payouts: payoutsTh,
    kobutsu: kobutsuTh,
    pos: posTh,
    tax: taxTh,
    payments: paymentsTh,
    shipping: shippingTh,
    settings: settingsTh,
    staff: staffTh,
    system: systemTh,
    notifications: notificationsTh,
    help: helpTh,
  },
} as const;

function syncDocumentLanguage(language: string | undefined) {
  if (typeof document !== "undefined" && isAdminLocale(language)) {
    // <html lang> drives the per-locale font stack and the CJK/Thai letter-spacing rules (tokens.css)
    document.documentElement.lang = language;
  }
}

export function createI18n(initialLanguage?: AdminLocale) {
  const instance = i18next.createInstance();
  instance.on("languageChanged", (language) => {
    syncDocumentLanguage(instance.resolvedLanguage ?? language);
  });
  void instance
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      ...(initialLanguage ? { lng: initialLanguage } : {}),
      fallbackLng: "en",
      supportedLngs: [...ADMIN_LOCALES],
      nonExplicitSupportedLngs: true,
      defaultNS: "common",
      ns: [...NAMESPACES],
      interpolation: { escapeValue: false },
      returnNull: false,
      detection: {
        order: ["localStorage", "navigator"],
        lookupLocalStorage: LANGUAGE_STORAGE_KEY,
        caches: ["localStorage"],
      },
    });
  return instance;
}

export const i18n = createI18n();

export function isAdminLocale(value: unknown): value is AdminLocale {
  return typeof value === "string" && (ADMIN_LOCALES as readonly string[]).includes(value);
}

/** BCP-47 tag for `Intl` per staff language. */
export function intlLocale(language: string | undefined): string {
  switch (language) {
    case "ja":
      return "ja-JP";
    case "th":
      return "th-TH";
    default:
      return "en-US";
  }
}
